import { Instagram, Facebook, Youtube, Menu, X } from "lucide-react";
import { useEffect, useState, lazy, Suspense } from "react";
import {
  TranslationProvider,
  useTranslation,
  SUPPORTED_LANGS,
  DEFAULT_LANG,
} from "./i18n";
import Hero from "./components/Hero";
import WhatsAppAnimationSection from "./components/WhatsAppAnimationSection";
import ScannersSection from "./components/ScannersSection";
import HowItWorksSection from "./components/HowItWorksSection";
import TokensSection from "./components/TokensSection";
import { DOC_TOOL_SLUGS } from "./data/docTools";
import { langsForPath, isPathAvailable, localizedPath } from "./utils/langScope";
import { organizationSchema, websiteSchema, ORG_ID } from "./utils/schema";
import FaqSection from "./components/FaqSection";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useParams,
  useNavigate,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { LazyMotion } from "framer-motion";
import logo from "./WhappScan.png";
import { signupUrl } from "./utils/signup";

// Rutas partidas en chunks. Antes todas las páginas se importaban de forma
// ansiosa y el bundle único pesaba 2,47 MB en TODAS las rutas, con un LCP
// medido de 11,8 s en móvil. Quien entra a un artículo ya no descarga las
// páginas legales, ni las de sector, ni las herramientas.
const TermsAndConditions = lazy(() => import("./pages/legal/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const DataProcessingAgreement = lazy(() => import("./pages/legal/DataProcessingAgreement"));
const GDPRCompliance = lazy(() => import("./pages/legal/GDPRCompliance"));
const SectorLanding = lazy(() => import("./pages/sectors/SectorLanding"));
const BlogList = lazy(() => import("./pages/blog/BlogList"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const FreeExtract = lazy(() => import("./pages/free/FreeExtract"));
const IdScanner = lazy(() => import("./pages/free/IdScanner"));
const BusinessCardScanner = lazy(() => import("./pages/free/BusinessCardScanner"));
const DocToolPage = lazy(() => import("./pages/free/DocToolPage"));
const InvoiceChecker = lazy(() => import("./pages/free/InvoiceChecker"));
const AlloggiatiWeb = lazy(() => import("./pages/free/AlloggiatiWeb"));
const ApiDocs = lazy(() => import("./pages/free/ApiDocs"));
const MrzChecker = lazy(() => import("./pages/free/MrzChecker"));
const MrzReader = lazy(() => import("./pages/free/MrzReader"));
const SesHospedajes = lazy(() => import("./pages/free/SesHospedajes"));
const IncomeCalculator = lazy(() => import("./pages/free/IncomeCalculator"));
const VerifactuQr = lazy(() => import("./pages/free/VerifactuQr"));
const ScanToPdf = lazy(() => import("./pages/free/ScanToPdf"));
const SupplierInvoices = lazy(() => import("./pages/free/SupplierInvoices"));
const AmlRecordPage = lazy(() => import("./pages/free/AmlRecordPage"));
const VigilanceCheck = lazy(() => import("./pages/free/VigilanceCheck"));
const PayslipIncome = lazy(() => import("./pages/free/PayslipIncome"));

function Nav() {
  const { lang, t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Cerrar el menú al cambiar de página: si no, queda abierto sobre la página
  // nueva tras pulsar un enlace.
  useEffect(() => setOpen(false), [pathname]);

  const handleLangChange = (newLang) => {
    // Replace the language part of the URL
    const paths = pathname.split("/");
    paths[1] = newLang;
    navigate(paths.join("/"));
  };

  const scrollToSection = (sectionId) => {
    if (pathname === `/${lang}/` || pathname === `/${lang}`) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(`/${lang}/#${sectionId}`);
    }
  };

  return (
    <nav className="relative flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-white sticky top-0 z-50 shadow-sm">
      <div className="nav-left flex items-center gap-3">
        {/* Prevent the logo from being squashed on small screens */}
        <Link to={`/${lang}`} className="flex items-center gap-3">
          <img
            src={logo}
            // width/height reservan el hueco antes de que cargue la imagen. Sin
            // ellos, entre 640 y ~900 px el menú cabía en una línea hasta que
            // llegaba el logo y entonces saltaba a dos (CLS 0,24 medido a 768 px).
            width={700}
            height={150}
            className="h-10 sm:h-12 w-auto flex-shrink-0 object-contain"
            alt="WhappScan logo"
          />
        </Link>
      </div>
      {/* En móvil, hamburguesa.

          Antes los seis controles se envolvían en tres filas y ocupaban 115 px
          -- el 16 % del viewport en un Android de 360 px -- empujando la CTA
          principal fuera de pantalla. Además convivían dos llamadas casi
          idénticas ("Prueba gratis" y "Probar gratis") que competían entre sí.
          Aquí se deja fuera solo la acción principal y el resto se pliega. */}
      <div className="sm:hidden flex items-center gap-1 pl-2">
        <button
          onClick={() => {
            if (window.gtag) window.gtag("event", "signup_click", { method: "nav_mobile" });
            if (window.fbq) window.fbq("track", "Lead", { content_name: "signup_nav_mobile" });
            window.open(signupUrl(lang), "_blank", "noopener");
          }}
          className="px-4 py-2 rounded-full bg-gradient-to-r from-green-500 to-teal-600 text-white text-sm font-medium shadow whitespace-nowrap"
        >
          {t("nav.signup")}
        </button>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-2 -mr-1 text-gray-700"
        aria-label={open ? "Cerrar menú" : "Abrir menú"}
        aria-expanded={open}
      >
        {open ? <X size={24} /> : <Menu size={24} />}
      </button>
      </div>

      <div className="nav-right hidden sm:flex items-center flex-wrap gap-2 justify-end">
        <button
          onClick={() => scrollToSection("pricing")}
          className="px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
        >
          {t("pricing.title") || "Pricing"}
        </button>
        <Link
          to={`/${lang}/free`}
          className="px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
        >
          {t("nav.free") || "Free tool"}
        </Link>
        <Link
          to={localizedPath(lang, "blog")}
          className="px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-primary-600 transition-colors"
        >
          Blog
        </Link>
        <button
          onClick={() => {
            if (window.gtag) {
              window.gtag("event", "signup_click", { method: "nav" });
            }
            if (window.fbq) {
              window.fbq("track", "Lead", { content_name: "signup_nav" });
            }
            window.open(
              signupUrl(lang),
              "_blank",
              "noopener"
            );
          }}
          className="px-3 py-1 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-green-500 to-teal-600 text-white text-xs sm:text-sm shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all whitespace-nowrap"
        >
          {t("nav.signup")}
        </button>
        <div className="flex gap-2 mt-0">
          <button
            onClick={() => {
              if (window.gtag) {
                window.gtag("event", "login_click", { method: "nav" });
              }
              if (window.fbq) {
                window.fbq("track", "Lead", { content_name: "login_nav" });
              }
              window.open(
                `https://app.whappscan.com/login?lang=${lang}`,
                "_blank",
                "noopener"
              );
            }}
            className="px-3 py-1 sm:px-3 sm:py-1 border border-gray-200 rounded-full bg-white text-xs sm:text-sm hover:bg-gray-50 shadow-sm hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-100 whitespace-nowrap"
          >
            {t("nav.login")}
          </button>
          <div className="px-3 py-1 sm:px-3 sm:py-1 border border-gray-200 rounded-full bg-white text-xs sm:text-sm hover:bg-gray-50 shadow-sm hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-100 whitespace-nowrap">
            <select
              value={lang}
              onChange={(e) => handleLangChange(e.target.value)}
              className="bg-transparent border-none focus:ring-0 cursor-pointer outline-none"
            >
              <option value="es">🇪🇸 ES</option>
              <option value="en">🇬🇧 EN</option>
              <option value="de">🇩🇪 DE</option>
              <option value="fr">🇫🇷 FR</option>
              <option value="it">🇮🇹 IT</option>
              <option value="pt">🇵🇹 PT</option>
            </select>
          </div>
        </div>
      </div>

      {/* Panel desplegable. Solo se monta cuando está abierto: así no añade
          nodos al DOM inicial, que es el que mide el LCP. */}
      {open && (
        <div className="sm:hidden absolute top-full left-0 right-0 bg-white border-b shadow-lg">
          <div className="flex flex-col px-4 py-3 gap-1">
            <button
              onClick={() => {
                setOpen(false);
                scrollToSection("pricing");
              }}
              className="text-left px-3 py-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50"
            >
              {t("pricing.title") || "Pricing"}
            </button>
            <Link
              to={`/${lang}/free`}
              className="px-3 py-3 text-sm font-semibold text-primary-600 rounded-lg hover:bg-gray-50"
            >
              {t("nav.free") || "Free tool"}
            </Link>
            <Link
              to={localizedPath(lang, "blog")}
              className="px-3 py-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Blog
            </Link>
            <button
              onClick={() => {
                if (window.gtag) window.gtag("event", "login_click", { method: "nav_mobile" });
                window.open(`https://app.whappscan.com/login?lang=${lang}`, "_blank", "noopener");
              }}
              className="text-left px-3 py-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50"
            >
              {t("nav.login")}
            </button>
            <div className="px-3 py-2 border-t mt-1">
              <select
                value={lang}
                onChange={(e) => handleLangChange(e.target.value)}
                className="w-full bg-transparent border border-gray-200 rounded-lg px-3 py-2 text-sm cursor-pointer outline-none"
              >
                <option value="es">🇪🇸 Español</option>
                <option value="en">🇬🇧 English</option>
                <option value="de">🇩🇪 Deutsch</option>
                <option value="fr">🇫🇷 Français</option>
                <option value="it">🇮🇹 Italiano</option>
                <option value="pt">🇵🇹 Português</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

function LanguageWrapper() {
  const { lang } = useParams();
  const { pathname } = useLocation();

  if (!SUPPORTED_LANGS.includes(lang)) {
    return <Navigate to={`/${DEFAULT_LANG}`} replace />;
  }

  // de/fr no tienen blog ni páginas legales: esas rutas van a su versión en
  // inglés en vez de servir texto inglés bajo una URL alemana o francesa.
  const purePath = pathname.split("/").slice(2).join("/");
  if (!isPathAvailable(lang, purePath)) {
    return <Navigate to={`/en/${purePath}`} replace />;
  }

  return (
    <TranslationProvider lang={lang}>
      <LazyMotion features={() => import("./motionFeatures").then((mod) => mod.default)}>
      <SEO lang={lang} pathname={pathname} />
      <div className="font-sans">
        <Nav />
        <main>
          {/* Las rutas van en chunks aparte, así que hace falta un Suspense.
              El fallback se queda vacío a propósito: la cabecera y el pie ya
              están pintados y un spinner de milisegundos solo aporta parpadeo. */}
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </main>
        <Footer />
      </div>
      </LazyMotion>
    </TranslationProvider>
  );
}

// Locale de Open Graph por idioma. Facebook y LinkedIn esperan el formato
// `xx_XX`, no el código corto que usa el router.
const OG_LOCALE = { en: "en_US", es: "es_ES", it: "it_IT", pt: "pt_PT", de: "de_DE", fr: "fr_FR" };

/** Herramientas gratuitas enlazadas desde el pie (ver src/data/docTools.js). */
// invoice-checker solo existe en de/en; el filtro por idioma lo oculta en el resto.
const FOOTER_TOOLS = ["free", "invoice-checker", "alloggiati-web", "ses-hospedajes", "bank-statement-income-calculator", "comprobar-qr-verifactu", "gwg-identifizierung-ausweis-protokoll", "customer-id-record-austrac", "verifier-attestation-vigilance", "p60-payslip-to-excel", "scan-to-pdf", "supplier-invoices-to-excel", ...DOC_TOOL_SLUGS, "id-scanner", "mrz-checker", "mrz-reader", "business-card-scanner"];

/** Nombres de los sectores para el pie. Mismo orden en los cuatro idiomas. */
const SECTOR_LINKS = {
  accounting: { es: "Asesorías y contabilidad", en: "Accounting firms", it: "Studi contabili", pt: "Contabilidade", de: "Steuerberatung und Buchhaltung", fr: "Cabinets comptables" },
  realestate: { es: "Inmobiliarias", en: "Real estate", it: "Immobiliare", pt: "Imobiliárias", de: "Immobilien", fr: "Immobilier" },
  logistics: { es: "Logística y transporte", en: "Logistics", it: "Logistica", pt: "Logística", de: "Logistik und Transport", fr: "Logistique et transport" },
  legal: { es: "Despachos legales", en: "Legal", it: "Studi legali", pt: "Escritórios de advogados", de: "Kanzleien", fr: "Cabinets d'avocats" },
  insurance: { es: "Seguros", en: "Insurance", it: "Assicurazioni", pt: "Seguros", de: "Versicherungen", fr: "Assurances" },
};

function SEO({ lang, pathname }) {
  const { t } = useTranslation();

  // Helper to get alternative urls for hreflang
  const getAlternates = () => {
    const baseUrl = "https://whappscan.com"; // Change to your actual domain
    const purePath = pathname.split("/").slice(2).join("/"); // remove /lang/ part
    // Solo los idiomas en los que existe esta ruta (utils/langScope.js).
    return langsForPath(purePath).map((l) => (
      <link
        key={l}
        rel="alternate"
        hreflang={l}
        href={`${baseUrl}/${l}${purePath ? `/${purePath}` : ""}`}
      />
    ));
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{t("meta.title") || "WhappScan | AI Document Scanning"}</title>
      <meta
        name="description"
        content={
          t("meta.description") ||
          "Define document scanners and extract data via WhatsApp using OCR and AI-powered parsing."
        }
      />
      {getAlternates()}
      <link
        rel="alternate"
        hreflang="x-default"
        href={`https://whappscan.com/en${pathname.split("/").slice(2).join("/") ? `/${pathname.split("/").slice(2).join("/")}` : ""}`}
      />
      {/* Canonical tag */}
      <link rel="canonical" href={`https://whappscan.com${pathname}`} />

      {/* Open Graph / Twitter.
          No existía NINGUNA de estas etiquetas en las 284 URLs, así que cada
          enlace compartido por WhatsApp, LinkedIn o Facebook se veía como una
          URL desnuda: sin imagen, sin titular y sin descripción. En un producto
          que se distribuye por mensajería, eso es la carencia más cara del sitio.

          La imagen apunta a public/og-default.png, SIN hash de contenido. Un
          asset con hash de CRA cambia de nombre en cada build: es justo el error
          que dejaba el logo del publisher en 404 y rompería las tarjetas de
          previsualización en el siguiente despliegue.

          Las páginas que montan su propio <Helmet> (blog, sectores,
          herramientas) pueden sobreescribir estas etiquetas: react-helmet-async
          da prioridad al componente más profundo. */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="WhappScan" />
      <meta property="og:locale" content={OG_LOCALE[lang] || OG_LOCALE.en} />
      <meta property="og:title" content={t("meta.title")} />
      <meta property="og:description" content={t("meta.description")} />
      <meta property="og:url" content={`https://whappscan.com${pathname}`} />
      <meta property="og:image" content="https://whappscan.com/og-default.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="WhappScan" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t("meta.title")} />
      <meta name="twitter:description" content={t("meta.description")} />
      <meta name="twitter:image" content="https://whappscan.com/og-default.png" />
    </Helmet>
  );
}

function RootRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const userLang = navigator.language || navigator.userLanguage;
    const shortLang = userLang.split("-")[0];
    const targetLang = SUPPORTED_LANGS.includes(shortLang)
      ? shortLang
      : DEFAULT_LANG;
    navigate(`/${targetLang}`, { replace: true });
  }, [navigate]);

  return null;
}

// Hero component is now in src/components/Hero.jsx

// Removed PhoneConfigSection (no longer used). HowItWorksSection now imported as a component.

// TokensSection now moved to src/components/TokensSection.jsx

function Footer() {
  const { lang, t } = useTranslation();
  const { pathname } = useLocation();
  // El enlace a ConextCRM va SOLO en la home de cada idioma (/es, /en, /it, /pt).
  // Si se renderiza en el footer de todas las páginas se convierte en un enlace
  // "sitewide": 62 enlaces desde un único dominio, del mismo dueño y con anchor
  // de concordancia exacta. Google lo trata como un solo enlace en el mejor de
  // los casos, y como patrón artificial en el peor. Ver docs/seo/ en el repo
  // conextcrm (informe del 10/8/2026).
  const isHome = /^\/[a-z]{2}\/?$/.test(pathname);
  return (
    <footer className="relative mt-10 border-t bg-gray-950 text-gray-300">
      <div className="footer-bg" aria-hidden></div>
      <div className="max-w-6xl mx-auto px-6 py-10 relative z-10 grid grid-cols-1 md:grid-cols-5 gap-8 text-sm">
        <div>
          <div className="font-semibold text-white mb-2">WhappScan</div>
          <p className="text-gray-400 text-xs leading-relaxed mb-4">
            {t("footer.tagline", {
              defaultValue:
                "Escáneres configurables para automatizar la extracción de datos desde WhatsApp.",
            })}
          </p>
          {/* 44 px de zona táctil (eran 18 px); el margen negativo mantiene la alineación del icono. */}
          <div className="-ml-3 flex gap-1">
            <a
              href="https://www.instagram.com/whappscan/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Instagram" title="Instagram"
            >
              <Instagram size={18} />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61585612529153&locale=es_ES"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Facebook" title="Facebook"
            >
              <Facebook size={18} />
            </a>
            <a
              href="https://www.youtube.com/@WhappScan"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="YouTube" title="YouTube"
            >
              <Youtube size={18} />
            </a>
          </div>
        </div>
        <div>
          {/* Enlaces a las 5 páginas de sector.

              Iban en el pie a propósito: aparece en las 284 páginas, así que
              crea el camino de rastreo que no existía. Antes NINGUNA página del
              sitio enlazaba a ninguna /sector/*, y el resultado fue que Google
              dejó `realestate` en "Crawled - currently not indexed" y ni
              siquiera llegó a conocer `legal`. */}
          <div className="font-semibold text-white mb-2">
            {t("footer.sectors", { defaultValue: "Sectores" })}
          </div>
          <ul className="space-y-1 text-xs text-gray-400">
            {Object.entries(SECTOR_LINKS).map(([key, names]) => (
              <li key={key}>
                <Link to={`/${lang}/sector/${key}`} className="hover:text-white">
                  {names[lang] || names.en}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          {/* Herramientas gratuitas por tipo de documento. Sustituye a la columna
              «Características» (tres anclas a la portada): igual que la de
              sectores, al estar en el pie de las ~300 páginas crea el camino de
              rastreo hacia las páginas «X a Excel», que es donde se convierte. */}
          <div className="font-semibold text-white mb-2">{t("footer.tools")}</div>
          <ul className="space-y-1 text-xs text-gray-400">
            {FOOTER_TOOLS.filter((slug) => isPathAvailable(lang, slug)).map((slug) => (
              <li key={slug}>
                <Link to={`/${lang}/${slug}`} className="hover:text-white">
                  {t(`footer.toolLinks.${slug}`)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="font-semibold text-white mb-2">Legal</div>
          <ul className="space-y-1 text-xs text-gray-400">
            <li>
              <Link to={localizedPath(lang, "terms")} className="hover:text-white">
                Terms & Conditions
              </Link>
            </li>
            <li>
              <Link to={localizedPath(lang, "privacy")} className="hover:text-white">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to={localizedPath(lang, "cookies")} className="hover:text-white">
                Cookie Policy
              </Link>
            </li>
            <li>
              <Link to={localizedPath(lang, "dpa")} className="hover:text-white">
                Data Processing Agreement
              </Link>
            </li>
            <li>
              <Link to={localizedPath(lang, "gdpr")} className="hover:text-white">
                GDPR Compliance
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <div className="font-semibold text-white mb-2">
            {t("footer.support", { defaultValue: "Support" })}
          </div>
          <ul className="space-y-2 text-xs text-gray-400">
            <li>
              {/* min-w-0 + overflow-wrap:anywhere: una dirección de email no
                  tiene espacios donde partirse, y entre 768 y ~1000 px la
                  columna mide 118 px — se salía 26 px y daba scroll horizontal
                  a toda la página. */}
              <a
                href="mailto:hola@whappscan.com"
                className="hover:text-white flex items-center gap-2 min-w-0"
              >
                <span>📧</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">hola@whappscan.com</span>
              </a>
            </li>
            <li>
              <a
                href="https://wa.me/35794039904"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white flex items-center gap-2"
              >
                <span>💬</span> +357 94039904
              </a>
            </li>
          </ul>
          {/* Enlace al otro producto del equipo. Va SIN rel="nofollow" a propósito:
              es un enlace editorial real y su valor para el rastreo depende de eso.
              Tampoco lleva "noreferrer", para no perder el origen en la analítica.
              El anchor es la marca a secas ("ConextCRM"): con palabras clave
              ("ConextCRM — citas por WhatsApp") es concordancia exacta y, repetido
              en 4 idiomas, es la firma de un esquema de enlaces. La descripción
              va en el <p> de debajo, fuera del <a>. */}
          {isHome && (
            <div className="mt-6">
              <div className="font-semibold text-white mb-2">
                {t("footer.otherProducts", { defaultValue: "Also from our team" })}
              </div>
              <a
                href="https://conextcrm.es/"
                target="_blank"
                rel="noopener"
                className="text-xs text-gray-400 hover:text-white block"
              >
                {t("footer.conextLink", { defaultValue: "ConextCRM" })}
              </a>
              <p className="text-[11px] text-gray-500 leading-relaxed mt-1">
                {t("footer.conextDesc", {
                  defaultValue:
                    "Appointment booking and reminders over WhatsApp for local businesses.",
                })}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 py-4 border-t border-gray-900 text-center text-xs text-gray-500">
        <div>{t("footer.copyright")}</div>
      </div>
    </footer>
  );
}

function LandingPage() {
  const { hash } = useLocation();
  const { lang } = useTranslation();

  useEffect(() => {
    if (hash) {
      // Small timeout to ensure elements are rendered
      const timeoutId = setTimeout(() => {
        const id = hash.replace("#", "");
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "WhappScan",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web, Cloud, WhatsApp",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "EUR",
            },
            description:
              "Extracts data from invoices, receipts, delivery notes, quotes, purchase orders, ID documents and business cards sent over WhatsApp or uploaded, and exports it to Excel, CSV or an API.",
            publisher: { "@id": ORG_ID },
            // NO aggregateRating here on purpose. It previously declared
            // 4.8 from 120 ratings while no review exists anywhere on the site
            // and no review-collection mechanism exists in the codebase.
            // Google requires ratings to come from reviews genuinely collected
            // and shown on the page; unverifiable markup risks a manual action
            // against the whole domain, not just the loss of a rich result.
            // Only reinstate once real reviews are collected AND rendered.
          })}
        </script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema(lang))}</script>
      </Helmet>
      {/* El widget vive DENTRO del hero: `TryFreeSection` quedaba por debajo
          del pliegue y era la acción principal de toda la estrategia. */}
      <Hero withWidget />
      <WhatsAppAnimationSection />
      <ScannersSection />
      <HowItWorksSection />
      <TokensSection />
      <FaqSection />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Routes>
          {/* Root redirect to detected language */}
          <Route path="/" element={<RootRedirect />} />

          {/* Language wrapped routes */}
          <Route path="/:lang" element={<LanguageWrapper />}>
            <Route index element={<LandingPage />} />
            <Route path="sector/:sector" element={<SectorLanding />} />
            <Route path="free" element={<FreeExtract />} />
            <Route path="id-scanner" element={<IdScanner />} />
            <Route path="business-card-scanner" element={<BusinessCardScanner />} />
            <Route path="invoice-checker" element={<InvoiceChecker />} />
            <Route path="alloggiati-web" element={<AlloggiatiWeb />} />
            <Route path="api" element={<ApiDocs />} />
            <Route path="mrz-checker" element={<MrzChecker />} />
            <Route path="mrz-reader" element={<MrzReader />} />
            <Route path="ses-hospedajes" element={<SesHospedajes />} />
            <Route path="bank-statement-income-calculator" element={<IncomeCalculator />} />
            <Route path="comprobar-qr-verifactu" element={<VerifactuQr />} />
            <Route path="scan-to-pdf" element={<ScanToPdf />} />
            <Route path="supplier-invoices-to-excel" element={<SupplierInvoices />} />
            <Route path="gwg-identifizierung-ausweis-protokoll" element={<AmlRecordPage preset="de" slug="gwg-identifizierung-ausweis-protokoll" />} />
            <Route path="customer-id-record-austrac" element={<AmlRecordPage preset="au" slug="customer-id-record-austrac" />} />
            <Route path="verifier-attestation-vigilance" element={<VigilanceCheck />} />
            <Route path="p60-payslip-to-excel" element={<PayslipIncome />} />
            {DOC_TOOL_SLUGS.map((slug) => (
              <Route key={slug} path={slug} element={<DocToolPage slug={slug} />} />
            ))}
            <Route path="blog" element={<BlogList />} />
            <Route path="blog/:postId" element={<BlogPost />} />
            <Route path="terms" element={<TermsAndConditions />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="cookies" element={<CookiePolicy />} />
            <Route path="dpa" element={<DataProcessingAgreement />} />
            <Route path="gdpr" element={<GDPRCompliance />} />
          </Route>

          {/* Fallback for everything else */}
          <Route
            path="*"
            element={<Navigate to={`/${DEFAULT_LANG}`} replace />}
          />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
